import { useEffect, useState } from "react"
import { Portal } from "./portal"

export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  confirmVariant = "danger",
  onConfirm,
  onCancel,
}: {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  confirmVariant?: "danger" | "primary"
  onConfirm: () => void
  onCancel: () => void
}) {
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel()
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [open, onCancel])

  if (!open) return null

  return (
    <Portal>
      <div className="fixed inset-0 z-[60] grid place-items-center bg-ink/30 p-4 backdrop-blur-sm">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
          className="w-full max-w-sm animate-row-in rounded-[10px] border border-gold-500 bg-white p-6 text-ink shadow-xl shadow-ink/10"
        >
          <h2 id="confirm-title" className="font-display text-xl font-bold tracking-tight">
            {title}
          </h2>
          <p className="mt-2 text-sm leading-6 text-ink/60">{message}</p>
          <div className="mt-6 flex flex-col-reverse items-stretch justify-end gap-2 sm:flex-row sm:items-center sm:gap-3">
            <button
              type="button"
              onClick={onCancel}
              autoFocus
              className="w-full rounded-full border border-gold-500 px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-cream-100 sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className={`w-full rounded-full px-4 py-2 text-sm font-medium text-white transition-colors sm:w-auto ${
                confirmVariant === "primary"
                  ? "bg-forest-950 hover:bg-forest-900"
                  : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  )
}

export interface ConfirmOptions {
  title: string
  message: string
  confirmLabel?: string
}

/** Interstitial confirmation — resolves true only when the user confirms. */
export function useConfirm() {
  const [state, setState] = useState<(ConfirmOptions & { resolve: (ok: boolean) => void }) | null>(
    null,
  )

  const confirm = (options: ConfirmOptions) =>
    new Promise<boolean>((resolve) => setState({ ...options, resolve }))

  const close = (ok: boolean) => {
    state?.resolve(ok)
    setState(null)
  }

  const modal = (
    <ConfirmModal
      open={state !== null}
      title={state?.title ?? ""}
      message={state?.message ?? ""}
      confirmLabel={state?.confirmLabel}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  )

  return { confirm, modal }
}
